import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
	AuditEvent,
	ListEventsParams,
	listAuditEvents,
	countAuditEventsSince,
	buildAuditExportUrl,
	getRelatedAuditEvents,
} from "../../../services/AuditTimelineService";
import {
	BreadcrumbItem,
	BreadcrumbSeparator,
	DebtBreadcrumb,
	DebtContainer,
	DebtTitle,
} from "../DebtManagement/styled/DebtManagementStyles";
import { useAuditPageView, push } from "@whalelo/audit-client";
import ArchiveSearchDialog from "./ArchiveSearchDialog";

// ── Severity config ───────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<number, { label: string; color: string; bg: string; border: string }> = {
	1: { label: "Info", color: "#0369a1", bg: "#e0f2fe", border: "#0369a1" },
	2: { label: "Notice", color: "#15803d", bg: "#dcfce7", border: "#15803d" },
	3: { label: "Warning", color: "#b45309", bg: "#fef3c7", border: "#b45309" },
	4: { label: "Critical", color: "#b91c1c", bg: "#fee2e2", border: "#b91c1c" },
};

function getSeverityConfig(severity: number) {
	return SEVERITY_CONFIG[severity] || { label: `Sev${severity}`, color: "#475569", bg: "#f1f5f9", border: "#475569" };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format ISO string to HH:MM in VN timezone */
function formatTimeVn(value: string): string {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return new Intl.DateTimeFormat("vi-VN", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

/** Format ISO string to full VN datetime */
function formatDateTimeVn(value: string): string {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("vi-VN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

/** Return YYYY-MM-DD string in VN timezone for grouping */
function getVnDateKey(isoString: string): string {
	const date = new Date(isoString);
	if (Number.isNaN(date.getTime())) return "unknown";
	return new Intl.DateTimeFormat("sv-SE", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(date);
}

/** Format date key YYYY-MM-DD to human-readable header like "Today, Wednesday May 28" */
function formatDateGroupHeader(dateKey: string): string {
	if (dateKey === "unknown") return "Unknown date";
	const [year, , day] = dateKey.split("-").map(Number);
	const date = new Date(`${dateKey}T12:00:00+07:00`);

	const todayKey = getVnDateKey(new Date().toISOString());
	const yesterdayDate = new Date();
	yesterdayDate.setDate(yesterdayDate.getDate() - 1);
	const yesterdayKey = getVnDateKey(yesterdayDate.toISOString());

	const weekday = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date);
	const monthName = new Intl.DateTimeFormat("en-US", { month: "long" }).format(date);

	if (dateKey === todayKey) return `Today, ${weekday} ${monthName} ${day}`;
	if (dateKey === yesterdayKey) return `Yesterday, ${weekday} ${monthName} ${day}`;
	return `${weekday}, ${monthName} ${day}, ${year}`;
}

/** Group events by VN date key, preserving order (most recent first) */
function groupEventsByDate(events: AuditEvent[]): { dateKey: string; events: AuditEvent[] }[] {
	const map = new Map<string, AuditEvent[]>();
	for (const ev of events) {
		const key = getVnDateKey(ev.occurred_at);
		if (!map.has(key)) map.set(key, []);
		map.get(key)!.push(ev);
	}
	return Array.from(map.entries())
		.sort((a, b) => b[0].localeCompare(a[0]))
		.map(([dateKey, evs]) => ({ dateKey, events: evs }));
}

/** Returns ISO UTC string for VN timezone boundary */
function getVnTimeBoundary(range: "today" | "this_week" | "this_month" | "all", which: "start" | "end"): string | null {
	if (range === "all") return null;

	const vnNow = new Date();
	const vnDateStr = new Intl.DateTimeFormat("sv-SE", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		timeZone: "Asia/Ho_Chi_Minh",
	}).format(vnNow);
	const [yyyy, mm] = vnDateStr.split("-").map(Number);

	let startDate: Date;
	let endDate: Date;

	if (range === "today") {
		startDate = new Date(`${vnDateStr}T00:00:00+07:00`);
		endDate = new Date(`${vnDateStr}T23:59:59+07:00`);
	} else if (range === "this_week") {
		const dayOfWeek = vnNow.getDay();
		const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
		const monday = new Date(`${vnDateStr}T00:00:00+07:00`);
		monday.setDate(monday.getDate() + diffToMonday);
		startDate = monday;
		endDate = new Date(`${vnDateStr}T23:59:59+07:00`);
	} else {
		const firstDay = `${yyyy}-${String(mm).padStart(2, "0")}-01`;
		startDate = new Date(`${firstDay}T00:00:00+07:00`);
		endDate = new Date(`${vnDateStr}T23:59:59+07:00`);
	}

	return which === "start" ? startDate.toISOString() : endDate.toISOString();
}

// ── Filter state ──────────────────────────────────────────────────────────────

type TimeRangeKey = "today" | "this_week" | "this_month" | "all";

type FilterState = {
	actor_email: string;
	target_type: string;
	action: string;
	app: string;
	severity_min: string;
	since: string;
	until: string;
	timeRange: TimeRangeKey;
};

const EMPTY_FILTERS: FilterState = {
	actor_email: "",
	target_type: "",
	action: "",
	app: "",
	severity_min: "",
	since: "",
	until: "",
	timeRange: "all",
};

function filtersToParams(f: FilterState): ListEventsParams {
	const p: ListEventsParams = {};
	if (f.actor_email) p.actor_email = f.actor_email;
	if (f.target_type) p.target_type = f.target_type;
	if (f.action) p.action = f.action;
	if (f.app) p.app = f.app;
	if (f.severity_min) {
		const n = parseInt(f.severity_min, 10);
		if (Number.isFinite(n)) p.severity_min = n;
	}
	if (f.timeRange !== "all") {
		const s = getVnTimeBoundary(f.timeRange, "start");
		const e = getVnTimeBoundary(f.timeRange, "end");
		if (s) p.since = s;
		if (e) p.until = e;
	} else {
		if (f.since) p.since = f.since;
		if (f.until) p.until = f.until;
	}
	return p;
}

// ── DiffCell component ────────────────────────────────────────────────────────

function DiffCell({ diff }: { diff?: { field: string; before: any; after: any }[] }) {
	const [open, setOpen] = useState(false);
	if (!diff || diff.length === 0) return null;
	return (
		<div style={{ marginTop: 8 }}>
			<button
				onClick={() => setOpen((v) => !v)}
				style={{
					border: "1px solid #cbd5e1",
					borderRadius: 6,
					padding: "3px 10px",
					fontSize: 12,
					background: open ? "#f1f5f9" : "#fff",
					cursor: "pointer",
					color: "#334155",
				}}>
				{open ? "Hide details ▲" : "Show details ▼"}
			</button>
			{open && (
				<table style={{ marginTop: 8, borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
					<thead>
						<tr>
							{["Field", "Before", "After"].map((h) => (
								<th
									key={h}
									style={{
										padding: "3px 8px",
										textAlign: "left",
										fontWeight: 600,
										color: "#475569",
										borderBottom: "1px solid #e2e8f0",
									}}>
									{h}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{diff.map((d, i) => (
							<tr key={i}>
								<td style={diffCellStyle}>{d.field}</td>
								<td style={{ ...diffCellStyle, color: "#b91c1c" }}>{String(d.before ?? "-")}</td>
								<td style={{ ...diffCellStyle, color: "#15803d" }}>{String(d.after ?? "-")}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}

// ── SeverityBadge component ───────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: number }) {
	const meta = getSeverityConfig(severity);
	return (
		<span
			style={{
				display: "inline-block",
				padding: "2px 8px",
				borderRadius: 6,
				fontSize: 11,
				fontWeight: 700,
				letterSpacing: 0.5,
				color: meta.color,
				background: meta.bg,
			}}>
			{meta.label}
		</span>
	);
}

// ── EventRow component ────────────────────────────────────────────────────────

function EventRow({ ev, onClick }: { ev: AuditEvent; onClick: (ev: AuditEvent) => void }) {
	const meta = getSeverityConfig(ev.severity);

	const mainLine =
		ev.summary && ev.summary.trim()
			? ev.summary
			: [
					ev.actor_email || "System",
					ev.action || "performed action",
					ev.target_type || "",
					ev.target_id ? `#${ev.target_id}` : "",
			  ]
					.filter(Boolean)
					.join(" ");

	const secondaryLine = [ev.app, ev.category].filter(Boolean).join(" · ");

	return (
		<div
			onClick={() => onClick(ev)}
			style={{
				borderLeft: `4px solid ${meta.border}`,
				background: "#fff",
				borderRadius: "0 8px 8px 0",
				padding: "12px 16px",
				marginBottom: 8,
				boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
				cursor: "pointer",
				transition: "background 0.12s",
			}}
			onMouseEnter={(e) => {
				(e.currentTarget as HTMLDivElement).style.background = "#f8fafc";
			}}
			onMouseLeave={(e) => {
				(e.currentTarget as HTMLDivElement).style.background = "#fff";
			}}>
			{/* Header: badge + time */}
			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
				<SeverityBadge severity={ev.severity} />
				<span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>{formatTimeVn(ev.occurred_at)}</span>
			</div>
			{/* Main line */}
			<div style={{ fontSize: 14, color: "#0f172a", fontWeight: 500, lineHeight: 1.4, marginBottom: 2 }}>
				{mainLine}
			</div>
			{/* Secondary line */}
			{secondaryLine && <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>{secondaryLine}</div>}
			{/* Diff toggle */}
			<DiffCell diff={ev.diff} />
		</div>
	);
}

// ── DetailDrawer component ────────────────────────────────────────────────────

function DetailDrawer({
	event,
	relatedEvents,
	onClose,
	onFilterActor,
	onFilterTarget,
}: {
	event: AuditEvent | null;
	relatedEvents: AuditEvent[];
	onClose: () => void;
	onFilterActor: (email: string) => void;
	onFilterTarget: (targetType: string) => void;
}) {
	if (!event) return null;

	const meta = getSeverityConfig(event.severity);

	return (
		<>
			{/* Backdrop — click outside to close */}
			<div
				onClick={onClose}
				style={{
					position: "fixed",
					inset: 0,
					zIndex: 99,
					background: "transparent",
				}}
			/>
			{/* Drawer panel */}
			<div
				style={{
					position: "fixed",
					top: 0,
					right: 0,
					bottom: 0,
					width: 420,
					zIndex: 100,
					background: "#fff",
					boxShadow: "-4px 0 24px rgba(15,23,42,0.14)",
					overflowY: "auto",
					padding: "20px 24px",
					display: "flex",
					flexDirection: "column",
					gap: 16,
				}}>
				{/* Close button */}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<span style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>Event Detail</span>
					<button
						onClick={onClose}
						style={{
							border: "none",
							background: "none",
							fontSize: 22,
							cursor: "pointer",
							color: "#64748b",
							lineHeight: 1,
							padding: "0 4px",
						}}>
						&times;
					</button>
				</div>

				{/* Severity + time */}
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<SeverityBadge severity={event.severity} />
					<span style={{ fontSize: 12, color: "#64748b" }}>{formatDateTimeVn(event.occurred_at)}</span>
				</div>

				{/* Summary */}
				{event.summary && (
					<div
						style={{
							background: "#f8fafc",
							borderRadius: 8,
							padding: "10px 12px",
							fontSize: 14,
							color: "#0f172a",
							fontWeight: 500,
							borderLeft: `4px solid ${meta.border}`,
						}}>
						{event.summary}
					</div>
				)}

				{/* Fields table */}
				<table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
					<tbody>
						{[
							{ label: "ID", value: event.id },
							{ label: "Action", value: event.action },
							{ label: "Category", value: event.category },
							{ label: "App", value: event.app },
							{ label: "Req ID", value: event.req_id ?? "-" },
						].map(({ label, value }) => (
							<tr
								key={label}
								style={{ borderBottom: "1px solid #f1f5f9" }}>
								<td
									style={{
										padding: "6px 8px 6px 0",
										fontWeight: 600,
										color: "#64748b",
										whiteSpace: "nowrap",
										width: 100,
									}}>
									{label}
								</td>
								<td style={{ padding: "6px 0", color: "#0f172a", wordBreak: "break-all" }}>{value}</td>
							</tr>
						))}
						<tr style={{ borderBottom: "1px solid #f1f5f9" }}>
							<td
								style={{
									padding: "6px 8px 6px 0",
									fontWeight: 600,
									color: "#64748b",
									whiteSpace: "nowrap",
									width: 100,
								}}>
								Actor
							</td>
							<td style={{ padding: "6px 0" }}>
								{event.actor_email ? (
									<button
										onClick={() => onFilterActor(event.actor_email!)}
										style={{
											background: "none",
											border: "none",
											color: "#0369a1",
											cursor: "pointer",
											padding: 0,
											fontSize: 13,
											textDecoration: "underline",
										}}>
										{event.actor_email}
									</button>
								) : (
									<span style={{ color: "#94a3b8" }}>-</span>
								)}
							</td>
						</tr>
						<tr style={{ borderBottom: "1px solid #f1f5f9" }}>
							<td
								style={{
									padding: "6px 8px 6px 0",
									fontWeight: 600,
									color: "#64748b",
									whiteSpace: "nowrap",
									width: 100,
								}}>
								Target
							</td>
							<td style={{ padding: "6px 0" }}>
								{event.target_type ? (
									<button
										onClick={() => onFilterTarget(event.target_type!)}
										style={{
											background: "none",
											border: "none",
											color: "#0369a1",
											cursor: "pointer",
											padding: 0,
											fontSize: 13,
											textDecoration: "underline",
										}}>
										{event.target_type}
										{event.target_id ? ` #${event.target_id}` : ""}
									</button>
								) : (
									<span style={{ color: "#94a3b8" }}>-</span>
								)}
							</td>
						</tr>
					</tbody>
				</table>

				{/* Diff */}
				{event.diff && event.diff.length > 0 && (
					<div>
						<div style={{ fontWeight: 600, fontSize: 13, color: "#334155", marginBottom: 6 }}>Changes</div>
						<DiffCell diff={event.diff} />
					</div>
				)}

				{/* Related events */}
				{relatedEvents.length > 0 && (
					<div>
						<div style={{ fontWeight: 600, fontSize: 13, color: "#334155", marginBottom: 8 }}>
							Related events in this request ({relatedEvents.length})
						</div>
						{relatedEvents.map((rel) => {
							const relMeta = getSeverityConfig(rel.severity);
							return (
								<div
									key={rel.id}
									style={{
										borderLeft: `3px solid ${relMeta.border}`,
										padding: "8px 10px",
										marginBottom: 6,
										background: "#f8fafc",
										borderRadius: "0 6px 6px 0",
										fontSize: 12,
									}}>
									<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
										<SeverityBadge severity={rel.severity} />
										<span style={{ color: "#64748b" }}>{formatTimeVn(rel.occurred_at)}</span>
									</div>
									<div style={{ color: "#0f172a", fontWeight: 500 }}>{rel.summary || rel.action}</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</>
	);
}

// ── StatsCard component ───────────────────────────────────────────────────────

function StatsCard({
	label,
	value,
	accentColor,
	bgColor,
}: {
	label: string;
	value: string | number;
	accentColor?: string;
	bgColor?: string;
}) {
	return (
		<div
			style={{
				background: bgColor || "#fff",
				borderRadius: 12,
				padding: "18px 20px",
				boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
				borderLeft: `4px solid ${accentColor || "#0369a1"}`,
			}}>
			<div
				style={{
					fontSize: 12,
					fontWeight: 600,
					color: "#64748b",
					marginBottom: 6,
					textTransform: "uppercase",
					letterSpacing: 0.5,
				}}>
				{label}
			</div>
			<div style={{ fontSize: 24, fontWeight: 700, color: "#0f172a" }}>{value}</div>
		</div>
	);
}

// ── LiveBanner component ──────────────────────────────────────────────────────

function LiveBanner({ newCount, onRefresh }: { newCount: number; onRefresh: () => void }) {
	if (newCount <= 0) return null;
	return (
		<div
			onClick={onRefresh}
			style={{
				position: "sticky",
				top: 0,
				zIndex: 10,
				background: "#0f62fe",
				color: "#fff",
				padding: "10px 16px",
				borderRadius: 8,
				marginBottom: 12,
				cursor: "pointer",
				fontWeight: 600,
				fontSize: 14,
				textAlign: "center",
				boxShadow: "0 2px 8px rgba(15,62,254,0.35)",
				animation: "audit-banner-pulse 1.5s ease-in-out infinite",
			}}>
			&#9650; {newCount} new event{newCount !== 1 ? "s" : ""} — click to refresh
			<style>{`
				@keyframes audit-banner-pulse {
					0%, 100% { opacity: 1; }
					50% { opacity: 0.85; }
				}
			`}</style>
		</div>
	);
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function AuditTimelinePage() {
	const location = useLocation();

	// Page view tracking
	useAuditPageView(location.pathname, push);

	const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
	const [appliedFilters, setAppliedFilters] = useState<FilterState>(EMPTY_FILTERS);
	const [events, setEvents] = useState<AuditEvent[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showAdvanced, setShowAdvanced] = useState(false);

	// Stats
	const [todayCount, setTodayCount] = useState<number | null>(null);
	const [todayCountLoading, setTodayCountLoading] = useState(false);

	// Detail drawer
	const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
	const [relatedEvents, setRelatedEvents] = useState<AuditEvent[]>([]);

	// Live banner polling
	const [newCount, setNewCount] = useState(0);
	const latestOccurredAtRef = useRef<string | null>(null);
	const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Archive search dialog
	const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

	// Derived stats from loaded events
	const alertCount = events.filter((ev) => ev.severity >= 3).length;
	const hasMoreAlerts = nextCursor !== null;
	const activeUsers = new Set(events.map((ev) => ev.actor_email).filter(Boolean)).size;
	const lastActivity = events.length > 0 ? formatDateTimeVn(events[0].occurred_at) : "-";

	// Load today's count in parallel
	const loadTodayCount = useCallback(async () => {
		setTodayCountLoading(true);
		try {
			const since = getVnTimeBoundary("today", "start");
			if (since) {
				const count = await countAuditEventsSince(since);
				setTodayCount(count);
			}
		} catch {
			setTodayCount(null);
		} finally {
			setTodayCountLoading(false);
		}
	}, []);

	const load = useCallback(async (params: ListEventsParams, replace: boolean) => {
		setLoading(true);
		setError(null);
		try {
			const result = await listAuditEvents({ ...params, limit: 50 });
			if (replace) {
				setEvents(result.data);
				// Track the most recent event's occurred_at for live polling baseline
				latestOccurredAtRef.current = result.data.length > 0 ? result.data[0].occurred_at : null;
				setNewCount(0);
			} else {
				setEvents((prev) => [...prev, ...result.data]);
			}
			setNextCursor(result.nextCursor);
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.message || "Failed to load audit events.");
		} finally {
			setLoading(false);
		}
	}, []);

	// Initial load + today count (parallel)
	useEffect(() => {
		setNextCursor(null);
		load(filtersToParams(appliedFilters), true);
		loadTodayCount();
	}, [appliedFilters, load, loadTodayCount]);

	// Live polling: check for new events every 30s
	useEffect(() => {
		const scheduleNext = () => {
			pollTimerRef.current = setTimeout(async () => {
				const since = latestOccurredAtRef.current;
				if (since) {
					try {
						const count = await countAuditEventsSince(since);
						// subtract 1 since the baseline event itself is included in count
						setNewCount(Math.max(0, count - 1));
					} catch {
						// ignore poll errors silently
					}
				}
				scheduleNext();
			}, 30000);
		};
		scheduleNext();
		return () => {
			if (pollTimerRef.current != null) {
				clearTimeout(pollTimerRef.current);
			}
		};
	}, []);

	// Open detail drawer and load related events
	const handleEventClick = useCallback(async (ev: AuditEvent) => {
		setSelectedEvent(ev);
		setRelatedEvents([]);
		const related = await getRelatedAuditEvents(ev.id);
		setRelatedEvents(related.filter((r) => r.id !== ev.id));
	}, []);

	const handleCloseDrawer = useCallback(() => {
		setSelectedEvent(null);
		setRelatedEvents([]);
	}, []);

	// Clickable actor sets Who? filter and applies
	const handleFilterActor = useCallback(
		(email: string) => {
			const next: FilterState = { ...EMPTY_FILTERS, actor_email: email };
			setFilters(next);
			setAppliedFilters(next);
			handleCloseDrawer();
		},
		[handleCloseDrawer]
	);

	// Clickable target sets Target type filter and applies
	const handleFilterTarget = useCallback(
		(targetType: string) => {
			const next: FilterState = { ...EMPTY_FILTERS, target_type: targetType };
			setFilters(next);
			setAppliedFilters(next);
			handleCloseDrawer();
		},
		[handleCloseDrawer]
	);

	const onApply = useCallback(() => {
		setAppliedFilters(filters);
	}, [filters]);

	const onReset = useCallback(() => {
		setFilters(EMPTY_FILTERS);
		setAppliedFilters(EMPTY_FILTERS);
		setShowAdvanced(false);
	}, []);

	const onLoadMore = useCallback(() => {
		if (!nextCursor) return;
		load({ ...filtersToParams(appliedFilters), cursor: nextCursor, limit: 50 }, false);
	}, [nextCursor, appliedFilters, load]);

	const onTimeRange = useCallback((range: TimeRangeKey) => {
		setFilters((prev) => ({ ...prev, timeRange: range, since: "", until: "" }));
	}, []);

	// Banner refresh: reload from top
	const handleBannerRefresh = useCallback(() => {
		setNextCursor(null);
		load(filtersToParams(appliedFilters), true);
		loadTodayCount();
	}, [appliedFilters, load, loadTodayCount]);

	const exportUrl = buildAuditExportUrl(filtersToParams(appliedFilters));
	const grouped = groupEventsByDate(events);

	const alertDisplay = hasMoreAlerts ? `${alertCount}+` : String(alertCount);
	const alertAccent = alertCount > 0 ? "#b45309" : "#15803d";
	const alertBg = alertCount > 0 ? "#fff7ed" : "#f0fdf4";

	return (
		<DebtContainer>
			<DebtBreadcrumb>
				<BreadcrumbItem>Admin</BreadcrumbItem>
				<BreadcrumbSeparator
					src="./static/img/direction-right.png"
					alt=">"
				/>
				<BreadcrumbItem>Activity Timeline</BreadcrumbItem>
			</DebtBreadcrumb>

			{/* Page header with title + Search Archive + Export CSV */}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
				<DebtTitle style={{ margin: 0 }}>Activity Timeline</DebtTitle>
				<div style={{ display: "flex", gap: 8 }}>
					<button
						onClick={() => setArchiveDialogOpen(true)}
						style={secondaryButtonStyle}>
						Search Archive
					</button>
					<a
						href={exportUrl}
						target="_blank"
						rel="noopener noreferrer"
						style={exportButtonStyle}>
						Export CSV
					</a>
				</div>
			</div>

			{/* Section 1 — Stats Cards */}
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
					gap: 16,
					marginBottom: 20,
				}}>
				<StatsCard
					label="Events Today"
					value={todayCountLoading ? "…" : todayCount !== null ? todayCount : "-"}
					accentColor="#0369a1"
				/>
				<StatsCard
					label="Alerts"
					value={alertDisplay}
					accentColor={alertAccent}
					bgColor={alertBg}
				/>
				<StatsCard
					label="Active Users"
					value={activeUsers}
					accentColor="#0369a1"
				/>
				<StatsCard
					label="Last Activity"
					value={lastActivity}
					accentColor="#0369a1"
				/>
			</div>

			{/* Section 2 — Filter Bar */}
			<div style={cardStyle}>
				{/* Primary filters */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr auto auto",
						gap: 12,
						alignItems: "end",
					}}>
					{/* Who? */}
					<div>
						<div style={labelStyle}>Who?</div>
						<input
							value={filters.actor_email}
							onChange={(e) => setFilters((prev) => ({ ...prev, actor_email: e.target.value }))}
							placeholder="Email address"
							style={inputStyle}
						/>
					</div>

					{/* Time range quick buttons */}
					<div>
						<div style={labelStyle}>Time range</div>
						<div style={{ display: "flex", gap: 4 }}>
							{(
								[
									{ key: "today", label: "Today" },
									{ key: "this_week", label: "This week" },
									{ key: "this_month", label: "This month" },
									{ key: "all", label: "All time" },
								] as { key: TimeRangeKey; label: string }[]
							).map(({ key, label }) => (
								<button
									key={key}
									onClick={() => onTimeRange(key)}
									style={{
										...timeRangeButtonStyle,
										background: filters.timeRange === key ? "#0f62fe" : "#fff",
										color: filters.timeRange === key ? "#fff" : "#334155",
										borderColor: filters.timeRange === key ? "#0f62fe" : "#cbd5e1",
									}}>
									{label}
								</button>
							))}
						</div>
					</div>

					{/* Apply / Reset / Advanced toggle */}
					<div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
						<button
							onClick={onApply}
							style={primaryButtonStyle}>
							Apply
						</button>
						<button
							onClick={onReset}
							style={secondaryButtonStyle}>
							Reset
						</button>
						<button
							onClick={() => setShowAdvanced((v) => !v)}
							style={secondaryButtonStyle}>
							Advanced filters {showAdvanced ? "▲" : "▼"}
						</button>
					</div>
				</div>

				{/* Advanced filters panel */}
				{showAdvanced && (
					<div
						style={{
							marginTop: 16,
							paddingTop: 16,
							borderTop: "1px solid #e2e8f0",
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
							gap: 12,
						}}>
						{[
							{ key: "action", label: "Action", placeholder: "create, update…" },
							{ key: "target_type", label: "Target type", placeholder: "order, partner…" },
							{ key: "app", label: "App", placeholder: "admin, station…" },
						].map(({ key, label, placeholder }) => (
							<div key={key}>
								<div style={labelStyle}>{label}</div>
								<input
									value={(filters as any)[key]}
									onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }))}
									placeholder={placeholder}
									style={inputStyle}
								/>
							</div>
						))}
						<div>
							<div style={labelStyle}>Min severity</div>
							<select
								value={filters.severity_min}
								onChange={(e) => setFilters((prev) => ({ ...prev, severity_min: e.target.value }))}
								style={inputStyle}>
								<option value="">All</option>
								<option value="1">1 – Info</option>
								<option value="2">2 – Notice</option>
								<option value="3">3 – Warning</option>
								<option value="4">4 – Critical</option>
							</select>
						</div>
					</div>
				)}
			</div>

			{/* Section 3 — Activity Feed */}
			<div>
				{/* Live banner */}
				<LiveBanner
					newCount={newCount}
					onRefresh={handleBannerRefresh}
				/>

				{error && (
					<div
						style={{
							color: "#b91c1c",
							background: "#fee2e2",
							borderRadius: 8,
							padding: "10px 14px",
							marginBottom: 12,
							fontSize: 13,
						}}>
						{error}
					</div>
				)}

				{loading && events.length === 0 && (
					<div style={{ textAlign: "center", padding: "32px 0", color: "#64748b", fontSize: 14 }}>
						Loading events…
					</div>
				)}

				{!loading && events.length === 0 && (
					<div style={{ textAlign: "center", padding: "32px 0", color: "#64748b", fontSize: 14 }}>
						No events found for the current filters.
					</div>
				)}

				{grouped.map(({ dateKey, events: dayEvents }) => (
					<div key={dateKey}>
						{/* Date group header */}
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 12,
								margin: "20px 0 12px",
								color: "#64748b",
								fontSize: 12,
								fontWeight: 600,
								textTransform: "uppercase",
								letterSpacing: 0.8,
							}}>
							<div style={{ flex: "0 0 auto" }}>─── {formatDateGroupHeader(dateKey)}</div>
							<div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
						</div>

						{/* Events for this date */}
						{dayEvents.map((ev) => (
							<EventRow
								key={ev.id}
								ev={ev}
								onClick={handleEventClick}
							/>
						))}
					</div>
				))}

				{/* Load more / end indicator */}
				<div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
					{nextCursor && (
						<button
							onClick={onLoadMore}
							disabled={loading}
							style={primaryButtonStyle}>
							{loading ? "Loading…" : "Load more"}
						</button>
					)}
					{loading && events.length > 0 && <span style={{ fontSize: 13, color: "#64748b" }}>Loading…</span>}
					{!nextCursor && events.length > 0 && !loading && (
						<span style={{ fontSize: 13, color: "#64748b" }}>All events loaded.</span>
					)}
				</div>
			</div>

			{/* Detail Drawer */}
			<DetailDrawer
				event={selectedEvent}
				relatedEvents={relatedEvents}
				onClose={handleCloseDrawer}
				onFilterActor={handleFilterActor}
				onFilterTarget={handleFilterTarget}
			/>

			{/* Archive Search Dialog */}
			<ArchiveSearchDialog
				open={archiveDialogOpen}
				onClose={() => setArchiveDialogOpen(false)}
			/>
		</DebtContainer>
	);
}

// ── Styles ────────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
	background: "#fff",
	borderRadius: 16,
	padding: 16,
	boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
	marginBottom: 16,
};

const labelStyle: React.CSSProperties = {
	fontSize: 12,
	fontWeight: 600,
	marginBottom: 6,
	color: "#334155",
};

const inputStyle: React.CSSProperties = {
	width: "100%",
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 12px",
	fontSize: 14,
	background: "#fff",
	boxSizing: "border-box",
};

const primaryButtonStyle: React.CSSProperties = {
	border: "none",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#0f62fe",
	color: "#fff",
	fontWeight: 600,
	cursor: "pointer",
	fontSize: 14,
	whiteSpace: "nowrap" as const,
};

const secondaryButtonStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#fff",
	color: "#0f172a",
	fontWeight: 600,
	cursor: "pointer",
	fontSize: 14,
	whiteSpace: "nowrap" as const,
};

const exportButtonStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 10,
	padding: "10px 16px",
	background: "#fff",
	color: "#0f172a",
	fontWeight: 600,
	cursor: "pointer",
	fontSize: 14,
	textDecoration: "none",
	display: "inline-block",
	whiteSpace: "nowrap" as const,
};

const timeRangeButtonStyle: React.CSSProperties = {
	border: "1px solid #cbd5e1",
	borderRadius: 8,
	padding: "8px 12px",
	fontSize: 13,
	fontWeight: 500,
	cursor: "pointer",
	whiteSpace: "nowrap" as const,
	transition: "all 0.15s",
};

const diffCellStyle: React.CSSProperties = {
	padding: "3px 8px",
	fontSize: 12,
	color: "#0f172a",
};
